import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { MenubarModule } from 'primeng/menubar';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../core/auth/services/auth.service';
import { NotificationBellComponent } from './notification-bell.component';
import { TokenStorageService } from '../../../core/auth/services/token-storage.service';
import { UtentiService } from '../../../features/utenti/services/utenti.service';
import { Account } from '../../../features/utenti/models/account.model';
import { RoleLabelPipe } from '../../../shared/pipes/role-label.pipe';
import { UserService } from '../../../core/auth/services/user.service';

@Component({
  selector: 'app-navbar',
  standalone: true,
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.css'],
  imports: [
    RouterModule,
    CommonModule,
    MenubarModule,
    ButtonModule,
    InputTextModule,
    MenuModule,
    NotificationBellComponent
  ]
})
export class NavbarComponent implements OnInit {
  private authService = inject(AuthService);
  private tokenStorage = inject(TokenStorageService);
  private utentiService = inject(UtentiService);
  public userService = inject(UserService);
  private router = inject(Router);

  @Output() menuToggle = new EventEmitter<void>();

  userItems: MenuItem[] = [];
  mainMenuItems: MenuItem[] = [];
  themeIcon: string = 'pi pi-sun';
  currentAccount: Account | null = null;
  loadingAccount = false;

  ngOnInit() {
    // Read theme preference from localStorage on init
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme === 'true') {
      document.documentElement.classList.add('my-app-dark');
    } else {
      document.documentElement.classList.remove('my-app-dark');
    }

    this.loadAccountInfo();
    this.updateUserMenu();

    // Menu diverso per tecnici (solo Clienti)
    if (this.userService.isTechnician()) {
      this.mainMenuItems = [
        {
          label: 'Clienti',
          routerLink: '/lead'
        }
      ];
    } else {
      this.mainMenuItems = [
        {
          label: 'Dashboard',
          routerLink: '/dashboard'
        },
        {
          label: 'Clienti',
          routerLink: '/lead'
        },
        {
          label: 'Trattative',
          routerLink: '/deal'
        },
        {
          label: 'Calendario Task',
          routerLink: '/task'
        },
        {
          label: 'Elenco Task',
          routerLink: '/task-list'
        },
        {
          label: 'Prodotti',
          routerLink: '/prodotti'
        }
      ];
      // Solo admin: gestione venditori (inutile per account vendor)
      if (this.userService.isAdmin()) {
        this.mainMenuItems.push({
          label: 'Venditori',
          routerLink: '/venditori'
        });
      }
    }

    this.updateThemeIcon();

  }

  logout(): void {
    this.authService.logout();
  }

  toggleTheme(): void {
    const htmlElement = document.documentElement;
    htmlElement.classList.toggle('my-app-dark');
    const isDarkMode = htmlElement.classList.contains('my-app-dark');
    localStorage.setItem('darkMode', String(isDarkMode));
    this.updateThemeIcon();
  }

  updateThemeIcon(): void {
    const isDarkMode = document.documentElement.classList.contains('my-app-dark');
    this.themeIcon = isDarkMode ? 'pi pi-moon' : 'pi pi-sun';
  }

  // Funzione per navigare alle impostazioni
  goToSettings() {
    this.router.navigate(['/impostazioni']);
  }

  /**
   * Carica le informazioni dell'account corrente dallo storage
   */
  loadAccountInfo(): void {
    const accountId = this.tokenStorage.getAccountId();
    const firstName = this.tokenStorage.getAccountFirstName();
    const lastName = this.tokenStorage.getAccountLastName();
    const role = this.tokenStorage.getRole();
    
    if (accountId && (firstName || lastName)) {
      // Usa i dati dallo storage
      this.currentAccount = {
        account_id: accountId,
        account_first_name: firstName || '',
        account_last_name: lastName || '',
        account_email: '',
        account_phone_number: '',
        account_role: role || ''
      };
      this.updateUserMenu();
    } else if (accountId) {
      // Fallback: chiama l'API solo se i dati non sono nello storage
      this.loadingAccount = true;
      this.utentiService.getAccountById(accountId).subscribe({
        next: (response) => {
          if (response.isSuccess && response.data) {
            this.currentAccount = response.data;
            // Salva anche nello storage per prossime volte
            if (response.data.account_first_name) {
              this.tokenStorage.saveAccountFirstName(response.data.account_first_name);
            }
            if (response.data.account_last_name) {
              this.tokenStorage.saveAccountLastName(response.data.account_last_name);
            }
          }
          this.loadingAccount = false;
          this.updateUserMenu();
        },
        error: () => {
          this.loadingAccount = false;
          this.updateUserMenu();
        }
      });
    }
  }

  /**
   * Aggiorna il menu utente con le informazioni dell'account
   */
  updateUserMenu(): void {
    const items: MenuItem[] = [];

    // Aggiungi informazioni account se disponibili
    if (this.currentAccount) {
      items.push({
        label: `${this.currentAccount.account_first_name} ${this.currentAccount.account_last_name}`,
        disabled: true,
        styleClass: 'font-semibold'
      });
      const rolePipe = new RoleLabelPipe();
      items.push({
        label: rolePipe.transform(this.currentAccount.account_role) || this.currentAccount.account_role,
        disabled: true,
        styleClass: 'text-sm text-color-secondary'
      });
      items.push({ separator: true });
    } else if (this.loadingAccount) {
      items.push({
        label: 'Caricamento...',
        disabled: true
      });
      items.push({ separator: true });
    }

    // Aggiungi pulsante logout
    items.push({
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    });

    this.userItems = items;
  }
}